import FlowSubjectRow from "../features/calculator/components/FlowSubjectRow";
import FlowChoice from "../features/calculator/components/FlowChoice";
import FlowLevelSelect from "../features/calculator/components/FlowLevelSelect";
import useFlowDefaults from "../features/calculator/hooks/useFlowDefaults";
import React, { useState, useEffect, useMemo } from "react";
import { useCalculatorStoredValue } from "../contexts/CalculatorPersistenceContext";
import { useTZ } from "../contexts/TimezoneContext";
import { useGradeActions } from "../contexts/GradesContext";
import GroupTwo from "./GroupTwo";
import GroupThree from "./GroupThree";
import GroupFour from "./GroupFour";
import { getEntryForSubject } from "../utils/timezone";
import { getBoundaryResolutionOrder } from "../utils/boundaryCatalog";
import useBoundaryCatalog from "../hooks/useBoundaryCatalog";
import { getBoundarySubjectMeta } from "../data/subjectCatalog";
import { getPointsAboveBoundary } from "../features/subjects/utils/subjectScoring";

const COMPONENTS = {
  "Dance_SL": [
    ["ca", "Comp. & Analysis", 20, "40%"],
    ["di", "Dance Inv.", 20, "20%"],
    ["perf", "Performance", 22, "40%"],
  ],
  "Dance_HL": [
    ["ca", "Comp. & Analysis", 30, "35%"],
    ["di", "Dance Inv.", 25, "25%"],
    ["perf", "Performance", 22, "40%"],
  ],
  "Film_SL": [
    ["ta", "Text. Analysis", 28, "30%"],
    ["cs", "Comp. Study", 32, "30%"],
    ["fp", "Film Portfolio", 24, "40%"],
  ],
  "Film_HL": [
    ["ta", "Text. Analysis", 28, "20%"],
    ["cs", "Comp. Study", 32, "20%"],
    ["fp", "Film Portfolio", 24, "25%"],
    ["cfp", "Collab. Film", 24, "35%"],
  ],
  "Music_SL": [
    ["emc", "Music in Context", 24, "30%"],
    ["ewm", "Exp. w/ Music", 28, "30%"],
    ["pm", "Presenting Music", 38, "40%"],
  ],
  "Music_HL": [
    ["emc", "Music in Context", 24, "20%"],
    ["ewm", "Exp. w/ Music", 28, "20%"],
    ["cmm", "Music-Maker", 32, "30%"],
    ["pm", "Presenting Music", 38, "30%"],
  ],
  "Theatre_SL": [
    ["rp", "Research Pres.", 24, "30%"],
    ["cp", "Collab. Project", 24, "40%"],
    ["pp", "Prod. Proposal", 20, "30%"],
  ],
  "Theatre_HL": [
    ["rp", "Research Pres.", 24, "20%"],
    ["cp", "Collab. Project", 24, "25%"],
    ["stp", "Solo Piece", 24, "35%"],
    ["pp", "Prod. Proposal", 20, "20%"],
  ],
  "Visual Arts_SL": [
    ["cs", "Comp. Study", 30, "20%"],
    ["pp", "Process Portfolio", 34, "40%"],
    ["ex", "Exhibition", 30, "40%"],
  ],
  "Visual Arts_HL": [
    ["cs", "Comp. Study", 42, "20%"],
    ["pp", "Process Portfolio", 34, "40%"],
    ["ex", "Exhibition", 30, "40%"],
  ],
  "Literature and Performance_SL": [
    ["p1", "Paper 1", 30, "30%"],
    ["wa", "Written Assign.", 26, "30%"],
    ["tpio", "Trans. Perf. & IO", 32, "40%"],
  ],
};

const normalizeSpaces = (value) => value.replace(/\s+/g, " ").trim();
const normalizeForMatch = (value) =>
  normalizeSpaces(value).replace(/\bAnd\b/gi, "and").toLowerCase();

const COMPONENT_SUBJECTS = Object.keys(COMPONENTS).reduce((acc, key) => {
  const match = key.match(/^(.*)_(SL|HL)$/);
  if (!match) return acc;
  const subject = match[1];
  const level = match[2];
  const normalized = normalizeForMatch(subject);
  if (!acc[normalized]) acc[normalized] = { subject, levels: new Set() };
  acc[normalized].levels.add(level);
  return acc;
}, {});

const parseGroupSixSubjectKey = (key) => {
  const match = key.match(/^(SL|HL)\s+(.+)$/i);
  if (!match) return null;
  const level = match[1].toUpperCase();
  const subjectPart = normalizeSpaces(match[2]);
  const lookup = COMPONENT_SUBJECTS[normalizeForMatch(subjectPart)];
  if (!lookup || !lookup.levels.has(level)) return null;
  return { key, level, subject: lookup.subject };
};

const GROUP_OPTIONS = [
  ["6", "An arts subject"],
  ["2", "Another language"],
  ["3", "Another humanities subject"],
  ["4", "Another science"],
];

const LEVELS = ["SL", "HL"];

function GroupSix({ boundary = "m24", onBoundaryNotFound, headerExtras = null }) {
  const { tz } = useTZ();
  const { updateGrade } = useGradeActions();
  const { dataByBoundary, catalogKeys, isLoading } = useBoundaryCatalog(boundary, {
    includeAllBoundaries: true,
  });

  const storagePath = "groups.6.groupSix";
  const [selectedGroup, setSelectedGroup] = useCalculatorStoredValue(`${storagePath}.selectedGroup`, "6");
  const [subject, setSubject] = useCalculatorStoredValue(`${storagePath}.subject`, "");
  const [level, setLevel] = useCalculatorStoredValue(`${storagePath}.level`, "");
  const [scores, setScores] = useCalculatorStoredValue(`${storagePath}.scores`, {});
  useFlowDefaults(storagePath, "groupSix", !isLoading && selectedGroup === "6");
  const [boundaryNotFound, setBoundaryNotFound] = useState(false);

  const handleGroupChange = (value) => {
    setSelectedGroup(value);
    setSubject("");
    setLevel("");
    setScores({});
    updateGrade("6", null, null, null);
  };

  const parsedSubjects = catalogKeys
    .filter((key) => getBoundarySubjectMeta(key)?.groupId === "6")
    .map(parseGroupSixSubjectKey)
    .filter(Boolean);

  const levelsBySubject = parsedSubjects.reduce((acc, item) => {
    if (!acc[item.subject]) acc[item.subject] = new Set();
    acc[item.subject].add(item.level);
    return acc;
  }, {});

  const subjectOptions = (
    level
      ? Object.keys(levelsBySubject).filter((s) => levelsBySubject[s]?.has(level))
      : Object.keys(levelsBySubject)
  ).sort((a, b) => a.localeCompare(b));

  const availableLevels = subject
    ? LEVELS.filter((l) => levelsBySubject[subject]?.has(l))
    : [];

  const componentKey = subject && level ? `${subject}_${level}` : null;
  const subjectKeyCandidates =
    subject && level
      ? parsedSubjects.filter((item) => item.subject === subject && item.level === level).map((item) => item.key)
      : [];
  const allSubjectKeyCandidates = Array.from(
    new Set([
      ...subjectKeyCandidates,
      ...(subject && level
        ? [
            `${level} ${subject}`,
            `${level} ${subject.replace(/\band\b/gi, "And")}`,
            `${level} ${subject.replace(/\bAnd\b/gi, "and")}`,
          ]
        : []),
    ])
  );

  const scoreFields = useMemo(
    () => (componentKey ? (COMPONENTS[componentKey] || []) : []),
    [componentKey]
  );

  const hasCompleteScores =
    selectedGroup === "6" &&
    !!componentKey &&
    scoreFields.length > 0 &&
    scoreFields.every(([k]) => scores[k] !== "");

  const boundaryOrder = getBoundaryResolutionOrder(boundary);

  let entry = null;
  let resolvedBoundary = null;
  if (selectedGroup === "6" && hasCompleteScores) {
    for (const cb of boundaryOrder) {
      const cd = dataByBoundary[cb];
      if (!cd) continue;
      let candidateEntry = null;
      for (const sk of allSubjectKeyCandidates) {
        const found = getEntryForSubject(cd.timezones, sk, tz).entry;
        if (found) { candidateEntry = found; break; }
      }
      if (candidateEntry) { entry = candidateEntry; resolvedBoundary = cb; break; }
    }
  }
  const entryAvailable = Boolean(entry);

  useEffect(() => {
    if (selectedGroup !== "6" || isLoading) {
      setBoundaryNotFound((prev) => (prev ? false : prev));
      return;
    }
    if (!hasCompleteScores) {
      setBoundaryNotFound((prev) => (prev ? false : prev));
      return;
    }
    if (!entryAvailable) {
      setBoundaryNotFound((prev) => (prev ? prev : true));
    } else {
      setBoundaryNotFound((prev) => (prev ? false : prev));
    }
  }, [selectedGroup, isLoading, hasCompleteScores, entryAvailable]);

  let grade = null;
  let neededText = null;
  let pointsAboveBoundary = null;
  let error = null;

  if (selectedGroup === "6" && hasCompleteScores && entry) {
    if (!scoreFields.length) {
      error = `No components found for ${subject} ${level}`;
    } else {
      const total = scoreFields.reduce((sum, [k, , max, wt]) => {
        return sum + (Number(scores[k]) / max) * (parseFloat(wt) / 100) * 100;
      }, 0);
      const cutoffs = Array.from({ length: 7 }, (_, i) => parseFloat(entry[String(i + 1)]));
      if (cutoffs.some(isNaN)) {
        error = `Invalid grade boundaries for ${subject} ${level}`;
      } else {
        for (let i = 6; i >= 0; i--) {
          if (total >= cutoffs[i]) {
            grade = i + 1;
            if (i < 6) {
              neededText = `Need ${(cutoffs[i + 1] - total).toFixed(1)} percentage points for grade ${i + 2}`;
            }
            break;
          }
        }

        if (grade !== null) {
          pointsAboveBoundary = getPointsAboveBoundary(total, entry, grade);
        }
      }
    }
  }

  useEffect(() => {
    if (selectedGroup !== "6" || !componentKey) return;

    setScores((currentScores) => {
      const nextScores = scoreFields.reduce(
        (acc, [k]) => ({ ...acc, [k]: currentScores[k] ?? "" }),
        {}
      );
      const keys = Object.keys(nextScores);
      const currentKeys = Object.keys(currentScores);
      const matchesCurrentScores =
        keys.length === currentKeys.length &&
        keys.every((key) => currentScores[key] === nextScores[key]);

      return matchesCurrentScores ? currentScores : nextScores;
    });
  }, [selectedGroup, componentKey, scoreFields, setScores]);

  useEffect(() => {
    if (selectedGroup !== "6" || isLoading) return;

    if (selectedGroup === "6" && grade !== null && subject && level) {
      updateGrade("6", grade, subject, level, neededText);
    } else {
      updateGrade("6", null, null, null);
    }
  }, [selectedGroup, isLoading, grade, subject, level, neededText, updateGrade]);

  const fallbackBoundaryNote =
    resolvedBoundary && resolvedBoundary !== boundary
      ? `Result based on boundary "${resolvedBoundary}" because "${boundary}" is unavailable for this subject.`
      : null;

  /* ── group selector toggle ── */
  const GroupSelector = () => (
    <FlowChoice label="Sixth subject"><select value={selectedGroup} onChange={e => handleGroupChange(e.target.value)}>{GROUP_OPTIONS.map(([value, label]) => <option key={value} value={value}>{label}</option>)}</select></FlowChoice>
  );

  /* ── delegate to other groups ── */
  if (selectedGroup === "2") {
    return (
      <GroupTwo
        groupId="6"
        boundary={boundary}
        onBoundaryNotFound={onBoundaryNotFound}
        headerExtras={headerExtras}
        afterTitleExtras={<GroupSelector />}
      />
    );
  }
  if (selectedGroup === "3") {
    return (
      <GroupThree
        groupId="6"
        boundary={boundary}
        onBoundaryNotFound={onBoundaryNotFound}
        headerExtras={headerExtras}
        afterTitleExtras={<GroupSelector />}
      />
    );
  }
  if (selectedGroup === "4") {
    return (
      <GroupFour
        groupId="6"
        boundary={boundary}
        onBoundaryNotFound={onBoundaryNotFound}
        headerExtras={headerExtras}
        afterTitleExtras={<GroupSelector />}
      />
    );
  }

  if (isLoading) {
    return <div className="calculator-pad"><p>Loading…</p></div>;
  }
  if (Object.keys(dataByBoundary).length === 0) {
    return <div className="calculator-pad"><p>Boundary data unavailable.</p></div>;
  }

  return (
    <FlowSubjectRow groupId="6" fields={scoreFields} scores={scores} onScoresChange={setScores} grade={grade} neededText={neededText} pointsAboveBoundary={pointsAboveBoundary} fallbackBoundaryNote={fallbackBoundaryNote} error={error || (boundaryNotFound ? "Boundary not available. Try another boundary (e.g., m24)." : null)} levelControl={<FlowLevelSelect groupId="6" value={level} options={availableLevels} onChange={value => { setLevel(value); setScores({}); }} />} choices={<>
      {headerExtras}<GroupSelector />
      <FlowChoice label="Subject"><select value={subject} onChange={e => { setSubject(e.target.value); setScores({}); }}><option value="" disabled>Subject</option>{subjectOptions.map(value => <option key={value} value={value}>{value}</option>)}</select></FlowChoice>
    </>} />
  );
}

export default React.memo(GroupSix);
